/**
 * Widget del gruppo "Commerciale": nuovi lead, lead per fase, provenienze,
 * offerte in corso. I conti dei lead sono quelli delle Statistiche Lead
 * (kpiLead.ts).
 */

import { FileText, Target, UserPlus } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { numeroIntero } from '../../../lib/dashboard/formato';
import { statisticheLead } from '../../../lib/dashboard/kpiLead';
import { leadPerFonte, nuoviLead, statoOfferte } from '../../../lib/dashboard/kpiWidget';
import { LEAD_STAGES, LEAD_STAGE_LABELS, type Lead } from '../../../types/leads.types';
import { useFonte, useOggi, useRicaricaFonte } from '../../../hooks/useFonte';
import { useLeadSettings } from '../../../store/useSettingsStore';
import { Andamento, Ciambella, Etichetta, Fonte, NumeroGrande, Pillola, StatoVuoto, Variazione } from './parti';
import type { PropsWidget } from './tipi';

const NESSUN_LEAD = { icona: UserPlus, testo: 'Ancora nessun lead', sotto: 'Arriveranno da Meta, Google e dal sito' };
const SETTIMANA_MS = 7 * 86_400_000;

/** Quanti lead sono arrivati in ognuna delle ultime `settimane` settimane. */
function leadPerSettimana(leads: readonly Lead[], settimane: number, adesso: number): number[] {
  return Array.from({ length: settimane }, (_, i) => {
    const fine = adesso - (settimane - 1 - i) * SETTIMANA_MS;
    return leads.filter((l) => {
      const t = Date.parse(l.created_at);
      return t > fine - SETTIMANA_MS && t <= fine;
    }).length;
  });
}

export function WidgetNuoviLead({ taglia, impostazioni }: PropsWidget<'nuovi-lead'>) {
  const lead = useFonte('lead');
  const ricarica = useRicaricaFonte();

  return (
    <Fonte stato={lead} onRiprova={() => ricarica('lead')}>
      {(tutti) => {
        if (tutti.length === 0) return <StatoVuoto {...NESSUN_LEAD} />;
        const adesso = Date.now();
        const c = nuoviLead(tutti, impostazioni.periodo, adesso);
        const giorni = impostazioni.periodo === 'settimana' ? 7 : 30;
        return (
          <div className="relative flex h-full flex-col justify-end">
            <Andamento valori={leadPerSettimana(tutti, 10, adesso)} className="absolute -bottom-4 -left-4 h-2/3 w-[calc(100%+2rem)]" />
            <div className="relative">
              <Etichetta>arrivati negli ultimi {giorni} giorni</Etichetta>
              <NumeroGrande valore={numeroIntero(c.valore)} taglia={taglia} className="mt-1" />
              <div className="mt-2">
                <Variazione percentuale={c.variazione} rispetto={`sui ${giorni} giorni prima`} />
              </div>
            </div>
          </div>
        );
      }}
    </Fonte>
  );
}

/** Le fasi ancora aperte; vinti e persi solo nella taglia grande. */
const FASI_APERTE = ['nuovo', 'contattato', 'qualificato', 'proposta'];

export function WidgetPipelineLead({ taglia }: PropsWidget<'pipeline-lead'>) {
  const lead = useFonte('lead');
  const ricarica = useRicaricaFonte();
  const { giorniRicontatto } = useLeadSettings();

  return (
    <Fonte stato={lead} onRiprova={() => ricarica('lead')}>
      {(tutti) => {
        if (tutti.length === 0) return <StatoVuoto {...NESSUN_LEAD} />;
        const s = statisticheLead(tutti, giorniRicontatto);
        const fasi = LEAD_STAGES.filter((f) => taglia === 'L' || FASI_APERTE.includes(f.key));
        const totale = Math.max(1, fasi.reduce((n, f) => n + (s.byStage[f.key] || 0), 0));
        return (
          <div className="flex h-full min-h-0 flex-col justify-center gap-3">
            <div className="flex h-3 overflow-hidden rounded-full bg-black/[0.06]">
              {fasi.map((f) => {
                const n = s.byStage[f.key] || 0;
                return n > 0 ? <div key={f.key} className={cn('h-full first:rounded-l-full last:rounded-r-full', f.dotClass)} style={{ width: `${(n / totale) * 100}%` }} title={`${LEAD_STAGE_LABELS[f.key]}: ${n}`} /> : null;
              })}
            </div>
            <ul className={cn('grid gap-2', taglia === 'L' ? 'grid-cols-3' : 'grid-cols-4')}>
              {fasi.map((f) => (
                <li key={f.key} className="min-w-0 rounded-2xl bg-white/80 px-2 py-1.5 shadow-sm ring-1 ring-violet-100">
                  <p className="flex items-center gap-1 truncate text-[10px] font-medium text-neutral-500">
                    <span className={cn('h-2 w-2 flex-shrink-0 rounded-full', f.dotClass)} /> {LEAD_STAGE_LABELS[f.key]}
                  </p>
                  <p className="font-display text-lg font-bold leading-tight">{s.byStage[f.key] || 0}</p>
                </li>
              ))}
            </ul>
          </div>
        );
      }}
    </Fonte>
  );
}

const COLORI_FONTI = ['text-fuchsia-500', 'text-amber-400', 'text-emerald-500', 'text-violet-500', 'text-orange-400', 'text-primary-500', 'text-lime-500', 'text-pink-400', 'text-neutral-400'];
const PUNTI_FONTI = ['bg-fuchsia-500', 'bg-amber-400', 'bg-emerald-500', 'bg-violet-500', 'bg-orange-400', 'bg-primary-500', 'bg-lime-500', 'bg-pink-400', 'bg-neutral-400'];

export function WidgetLeadPerFonte({ taglia, impostazioni }: PropsWidget<'lead-per-fonte'>) {
  const lead = useFonte('lead');
  const ricarica = useRicaricaFonte();

  return (
    <Fonte stato={lead} onRiprova={() => ricarica('lead')}>
      {(tutti) => {
        if (tutti.length === 0) return <StatoVuoto {...NESSUN_LEAD} />;
        const fonti = leadPerFonte(tutti, impostazioni.periodo, Date.now());
        if (fonti.length === 0) return <StatoVuoto icona={Target} testo="Nessun lead in questo periodo" />;
        const totale = fonti.reduce((s, f) => s + f.count, 0);
        const visibili = fonti.slice(0, taglia === 'L' ? 8 : 3);
        return (
          <div className="flex h-full min-h-0 items-center gap-4">
            <Ciambella
              dimensione={taglia === 'L' ? 128 : 84}
              fette={fonti.map((f, i) => ({ valore: f.count, colore: COLORI_FONTI[i % COLORI_FONTI.length] }))}
              centro={<><span className="font-display text-lg font-bold leading-none">{totale}</span><span className="text-[9px] text-neutral-500">lead</span></>}
            />
            <ul className="min-w-0 flex-1 space-y-1.5">
              {visibili.map((f, i) => (
                <li key={f.key} className="flex items-center gap-2 text-xs">
                  <span className={cn('h-2.5 w-2.5 flex-shrink-0 rounded-full', PUNTI_FONTI[i % PUNTI_FONTI.length])} />
                  <span className="min-w-0 flex-1 truncate font-medium">{f.label}</span>
                  <span className="font-display font-bold tabular-nums">{f.count}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      }}
    </Fonte>
  );
}

export function WidgetOfferteAperte({ taglia, impostazioni, contesto }: PropsWidget<'offerte-aperte'>) {
  const offerte = useFonte('offerte');
  const ricarica = useRicaricaFonte();
  const oggi = useOggi();

  return (
    <Fonte stato={offerte} onRiprova={() => ricarica('offerte')}>
      {(tutte) => {
        const s = statoOfferte(tutte, oggi, impostazioni.soloMie ? contesto.utenteId : null);
        if (s.inAttesa.length === 0 && s.daFatturare === 0 && s.scadute === 0) {
          return <StatoVuoto icona={FileText} testo="Nessuna offerta in corso" azione={taglia !== 'S' ? { etichetta: 'Crea offerta', percorso: '/price-lists' } : undefined} />;
        }
        const righe = taglia === 'L' ? 4 : 0;
        return (
          <div className="flex h-full min-h-0 flex-col justify-end gap-2">
            <div className="flex items-end gap-2">
              <NumeroGrande valore={s.inAttesa.length} taglia={taglia === 'L' ? 'M' : taglia} />
              <Etichetta className="pb-0.5">in attesa di risposta</Etichetta>
            </div>
            {taglia !== 'S' && (
              <div className="flex flex-wrap gap-1.5">
                <Pillola>{s.daFatturare} da fatturare</Pillola>
                {s.scadute > 0 && <Pillola>{s.scadute} scadute senza risposta</Pillola>}
              </div>
            )}
            {righe > 0 && (
              <ul className="min-h-0 space-y-1.5 overflow-hidden">
                {s.inAttesa.slice(0, righe).map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-2 rounded-2xl bg-white/[0.12] px-3 py-1.5 text-xs">
                    <span className="truncate font-semibold">{o.customers?.company_name || o.name}</span>
                    <span className="flex-shrink-0 opacity-70">
                      {o.valid_until ? `fino al ${o.valid_until.slice(8, 10)}/${o.valid_until.slice(5, 7)}` : 'senza scadenza'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }}
    </Fonte>
  );
}
